import { useState, useEffect, useRef } from 'react';
import Radar from 'radar-sdk-js'
import 'radar-sdk-js/dist/radar.css'
import { formatInTimeZone } from 'date-fns-tz'; // vanilla js can do this (toLocaleTimeString) but not cleanly
import { enUS } from 'date-fns/locale/en-US' // for formatInTimeZone
import { differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears, addDays, addWeeks, addMonths, addYears } from 'date-fns';

async function getEvents() {
  var url;
  if (import.meta.env.DEV) {
    url = "http://localhost:80";
  } else {
    url = "https://www.intactivistdirectory.com";
  }
  const response = await fetch(url + '/api/events');
  const json = await response.json();
  return json
}

function getDisplayTimezone(tz, referenceDate) {
  try {
    return formatInTimeZone(referenceDate, tz, 'zzz', { locale: enUS })
  } catch (error) {
    return tz
  }
}

function displayUnixTime(time, desired_tz, m_index) {
  const start = new Date(time.time_start * 1000)
  const end = new Date(time.time_end * 1000)
  const currentYear = new Date().getFullYear()

  const needsYear = start.getFullYear() !== currentYear || end.getFullYear() !== currentYear
  const dateFormat = needsYear ? 'EEEE, MMMM d, yyyy' : 'EEEE, MMMM d'

  const startDate = formatInTimeZone(start, desired_tz, dateFormat)
  const endDate = formatInTimeZone(end, desired_tz, dateFormat)
  const startTime = formatInTimeZone(start, desired_tz, 'h:mma')
  const endTime = formatInTimeZone(end, desired_tz, 'h:mma')

  const timeString = startDate === endDate
    ? `${startDate}, ${startTime} - ${endTime}`
    : `${startDate}, ${startTime} - ${endDate}, ${endTime}`

  if (m_index === 0) {
    const tzAbbreviation = getDisplayTimezone(desired_tz, start)
    return (
      <p className="font-normal text-red-800 dark:text-red-400/90 break-words">
        {timeString}{' '}
        <span className="relative cursor-help border-b border-dotted border-gray-400 group/tooltip">
          {tzAbbreviation}
          <a className="absolute z-10 inline-block px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm dark:bg-gray-700 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-opacity duration-300">
            {desired_tz}
          </a>
        </span>
      </p>
    )
  }

  return timeString
}

function makeExternalLink(link) {
  if (link.includes("https://") || link.includes("http://")) {
    return link
  } else {
    return "https://" + link
  }
}

function RadarMapPoint({ index, lat, lng }) {
  const mapRef = useRef(null);

  useEffect(() => {
    if (import.meta.env.DEV) {
      Radar.initialize('prj_test_pk_a12840313cb48f7dd25d7381bdfdc99fd4289620');
    } else {
      Radar.initialize('prj_live_pk_a698ea9ede6ee596dd8ea08398c5af69c8183d32');
    }

    const map = Radar.ui.map({
      container: index + "_map",
      center: [lng, lat],
      zoom: 17,
    });
    mapRef.current = map;

    Radar.ui.marker()
      .setLngLat([lng, lat])
      .addTo(map);

  }, []);

  return (
    <div className="mt-2" id="map-container" style={{ width: '100%', height: '40vh', position: 'relative' }}>
      <div id={index + "_map"} style={{ height: '100%', position: 'absolute', width: '100%' }} />
    </div>
  );
};

function handleRecurring(events) {
    var current_time = new Date();
    events.map((event, index) => {
      // post-processing
      // Increment the original time to bring it up to current
      var timeSince = () => void 0;
      var addTime = () => void 0;
      switch (event.recurring) {
        case 'daily':
          timeSince = differenceInDays;
          addTime = addDays;
          break;
        case 'weekly':
          timeSince = differenceInWeeks;
          addTime = addWeeks;
          break;
        case 'fortnightly':
          timeSince = (d1, d2) => { return Math.floor(differenceInWeeks(d1, d2) / 2) };
          addTime = (d1, d2) => { return addWeeks(d1, d2 * 2) };
          break;
        case 'monthly':
          timeSince = differenceInMonths;
          addTime = addMonths;
          break;
        case 'yearly':
          timeSince = differenceInYears;
          addTime = addYears;
          break;
      }
      event.times.map((time, m_index) => {
        // Always add however many elapsed time units
        // The check for if an event is displayed or out of date happens at the DB level, so that is ignored for recurring. DB will return all recurring events.
        // In almost all cases the original event time is expired.
        var time_start_date = new Date(time.time_start * 1000)
        var time_end_date = new Date(time.time_end * 1000)
        if (time_end_date.getTime() < current_time.getTime()) {
          // event ended, add time to bring us into displaying an upcoming time. Basically modulus current time, plus 1 unit of time. Will always give us something in the future, precisely the next event.
          // event ended but less than 1 unit of time from original? timeSinceOriginal will be 0 and we end up with just +1.
          var timeSinceOriginal = timeSince(current_time, time_end_date)
          event.times[m_index].time_start = addTime(time_start_date, timeSinceOriginal + 1).getTime() / 1000
          event.times[m_index].time_end = addTime(time_end_date, timeSinceOriginal + 1).getTime() / 1000
        }
      })
    })
}

function Search() {

  const [searchTerm, setSearchTerm] = useState('');
  const [events, setEvents] = useState('');
  const [showMap, setShowMap] = useState([[], []]); // id of events and map# to show
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [useLocalTimezone, setUseLocalTimezone] = useState(false);

  function newMapArr(e_index, m_index) {
    const newShowMap = JSON.parse(JSON.stringify(showMap));
    newShowMap[e_index][m_index] = newShowMap[e_index][m_index] ? 0 : 1;
    setShowMap(newShowMap)
  }

  var filteredEventsHTML =
    <div role="status">
      <svg aria-hidden="true" className="m-auto w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="none" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
      </svg>
      <span className="sr-only">Loading...</span>
    </div>



  useEffect(() => {
    getEvents()
      .then(result => {
        handleRecurring(result)
        result.sort((a, b) => { return a.times[0].time_start - b.times[0].time_start })
        setEvents(result)
        const tmp_arr = Array(result.length);
        result.map((event, index) => { (tmp_arr[index] = Array(event.times.length).fill(0)) })
        setShowMap(tmp_arr)
        // Makes a 2d array of 0s according to the returned /events api. This includes empties for online only times so could be slightly better.
        // xor to set show map
      })
      .catch(err => {
        console.error(err);
      });
  }, []);


  const linkIcon = (
    <svg className="min-w-5 h-5 me-1 text-gray-700 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" fillOpacity="0.7">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.213 9.787a3.391 3.391 0 0 0-4.795 0l-3.425 3.426a3.39 3.39 0 0 0 4.795 4.794l.321-.304m-.321-4.49a3.39 3.39 0 0 0 4.795 0l3.424-3.426a3.39 3.39 0 0 0-4.794-4.795l-1.028.961" />
    </svg>
  );

  const emailIcon = (
    <svg className="min-w-5 h-5 me-1 text-gray-700 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" fillOpacity="0.7">
      <path stroke="" d="M17 6h-2V5h1a1 1 0 1 0 0-2h-2a1 1 0 0 0-1 1v2h-.541A5.965 5.965 0 0 1 14 10v4a1 1 0 1 1-2 0v-4c0-2.206-1.794-4-4-4-.075 0-.148.012-.22.028C7.686 6.022 7.596 6 7.5 6A4.505 4.505 0 0 0 3 10.5V16a1 1 0 0 0 1 1h7v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3h5a1 1 0 0 0 1-1v-6c0-2.206-1.794-4-4-4Zm-9 8.5H7a1 1 0 1 1 0-2h1a1 1 0 1 1 0 2Z" />
    </svg>
  );

  const phoneIcon = (
    <svg className="min-w-5 h-5 me-1 text-gray-700 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" fillOpacity="0.7">
      <path stroke="" d="M7.978 4a2.553 2.553 0 0 0-1.926.877C4.233 6.7 3.699 8.751 4.153 10.814c.44 1.995 1.778 3.893 3.456 5.572 1.68 1.679 3.577 3.018 5.57 3.459 2.062.456 4.115-.073 5.94-1.885a2.556 2.556 0 0 0 .001-3.861l-1.21-1.21a2.689 2.689 0 0 0-3.802 0l-.617.618a.806.806 0 0 1-1.14 0l-1.854-1.855a.807.807 0 0 1 0-1.14l.618-.62a2.692 2.692 0 0 0 0-3.803l-1.21-1.211A2.555 2.555 0 0 0 7.978 4Z" />
    </svg>
  );

  const timeIcon = (
    <svg className="min-w-5 h-5 me-1 text-gray-700 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" fillOpacity="0.7">
      <path fillRule="evenodd" d="M6 5V4a1 1 0 1 1 2 0v1h3V4a1 1 0 1 1 2 0v1h3V4a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v2H3V7a2 2 0 0 1 2-2h1ZM3 19v-8h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm5-6a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2H8Z" clipRule="evenodd" />
    </svg>
  );

  const locationIcon = (
    <svg className="min-w-5 h-5 me-1 text-gray-700 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" fillOpacity="0.7">
      <path stroke="" fillRule="evenodd" d="M11.906 1.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L5.982 15.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM15 9.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z" clipRule="evenodd" />
    </svg>
  );

  const onlineIcon = (
    <svg className="min-w-5 h-5 me-1 text-gray-700 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" fillOpacity="0.7">
      <path stroke="" fillRule="evenodd" d="M11.906 1.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L5.982 15.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM15 9.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z" clipRule="evenodd" />
    </svg>
  );

  const recurringIcon = (
    <svg className="min-w-5 h-5 me-1 text-gray-700 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" fillOpacity="0.7">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3M3.22302 14C4.13247 18.008 7.71683 21 12 21c4.9706 0 9-4.0294 9-9 0-4.97056-4.0294-9-9-9-3.72916 0-6.92858 2.26806-8.29409 5.5M7 9H3V5" />
    </svg>
  );

  if (events != '') {
    const filteredEvents = events.filter(event =>
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    filteredEventsHTML = filteredEvents.map((event, index) => (
      // jsx
      <div key={index} href="#" className="group block mb-4 p-6 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-white dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-800">
        <div className="mb-2 flex items-center">
          {(event.icon && <img className="min-h-8 h-8 rounded-lg" src={"data:image/png;base64, " + event.icon} alt="entry icon" />) || <img className="min-h-5 h-5 rounded-lg dark:invert" src="/bird.svg" alt="entry icon" />}
          <h5 className="ml-2 text-base md:text-2xl font-normal tracking-tight text-gray-700 dark:text-white">{event.title}</h5>
        </div>
        {(event.url) &&
          <div className="flex">
            {linkIcon}
            <a className="font-normal text-blue-600 dark:text-blue-400 hover:underline truncate" href={makeExternalLink(event.url)} >{event.url}</a>
          </div>
        }
        {(event.email) &&
          <div className="flex">
            {emailIcon}
            <p className="font-normal text-gray-700 dark:text-gray-400 break-words">{event.email}</p>
          </div>
        }
        {(event.phone) &&
          <div className="flex">
            {phoneIcon}
            <p className="font-normal text-gray-700 dark:text-gray-400 break-words">{event.phone}</p>
          </div>
        }
        <div className="flex flex-col">
          {event.times.map((time, m_index) => {
            return (
              <div key={m_index} className={`${m_index !== 0 ? 'mt-2' : ''}`}>
                <div className="flex flex-row items-center">
                  {timeIcon}
                  {displayUnixTime(time, useLocalTimezone == true ? localTimezone : event.timezone, m_index)}
                </div>
                {(time.online_only != "on" && (time.location)) &&
                  <div>
                    <div className="flex flex-row items-center">
                      {locationIcon}
                      <p className="font-normal text-gray-700 dark:text-gray-400 break-words">{time.location}
                      </p>
                      {(time.latitude != 0 && time.longitude != 0) && <button type="button" onClick={() => newMapArr(index, m_index)} className="ml-3 px-2 py-0.5 me-1 font-normal text-gray-700 focus:outline-none bg-white rounded-lg border-2 border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700">Show map</button>}
                    </div>
                    {(showMap[index][m_index] == 1) && time.online_only != "on" && <RadarMapPoint index={index + '.' + m_index} lng={time.longitude} lat={time.latitude} />}
                  </div>}
                {(time.online_only == "on") &&
                  <div>
                    <div className="flex flex-row items-center">
                      {onlineIcon}
                      <p className="font-normal text-gray-700 dark:text-gray-400">Online</p>
                    </div>
                  </div>}
              </div>
            )
          })}
          {(event.recurring != "none") &&
            <div>
              <div className="flex flex-row items-center">
                {recurringIcon}
                <p className="font-normal text-gray-700 dark:text-gray-400">{event.recurring.charAt(0).toUpperCase() + event.recurring.slice(1)}</p>
              </div>
            </div>}
        </div>
        <hr className="my-2 border-1 border-gray-200 group-hover:border-gray-200 dark:border-gray-700 dark:group-hover:border-gray-700"></hr>
        <p className="mt-3 font-normal text-gray-700 dark:text-gray-400 whitespace-pre-wrap break-words">{event.description}</p>
      </div>
    ))
  }

  return (
    <div className="m-auto max-w-6xl w-3/4 left-1/2">
      <div className="relative mb-5">
        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
          <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
          </svg>
        </div>
        <input type="search" id="default-search" className="block w-full p-2 ps-10 text-m text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
          // volunteering opportunities, meetups, events, etc...
          placeholder="Search title and description"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div>
        <label className="inline-flex items-center mb-5 cursor-pointer">
          <input type="checkbox"
            checked={useLocalTimezone}
            onChange={e => setUseLocalTimezone(e.target.checked)}
            className="sr-only peer" />
          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:w-5 after:h-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
          <span className="ms-3 text-m text-gray-900 dark:text-gray-300">Local timezone</span>
        </label>
      </div>

      <div className="">
        {filteredEventsHTML}
      </div>
    </div>
  );
};

export default Search;